import React, { useState, useEffect } from 'react';
import { 
    IconButton, Chip, Box, TableRow, TableCell, Tooltip, Snackbar, Alert 
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DataTable from '../DataTable';
import * as API from '../../api';
import FinancialAccountDialog from './FinancialAccountDialog';

const ACCOUNT_TYPE_MAP = {
    'COMPANY': { label: 'Công ty', color: 'primary' },
    'PERSONAL': { label: 'Cá nhân', color: 'secondary' },
    'CASH': { label: 'Tiền mặt', color: 'default' }
};

const FinancialAccountTab = ({ openAddSignal, onCloseAdd }) => {
    const [data, setData] = useState([]);
    const [editItem, setEditItem] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    const loadData = async () => {
        try {
            const res = await API.fetchAllFinancialAccounts();
            setData(res.data);
        } catch (e) { console.error(e); }
    };

    useEffect(() => { loadData(); }, []);

    useEffect(() => {
        if (openAddSignal) {
            setEditItem(null);
            setIsOpen(true);
        }
    }, [openAddSignal]);

    const handleDelete = async (id) => {
        if (window.confirm("Bạn có chắc chắn muốn xóa tài khoản này?")) {
            try {
                await API.deleteFinancialAccount(id);
                loadData();
            } catch (e) { alert("Xóa thất bại"); }
        }
    };

    const handleCopy = (row) => {
        const textToCopy = `
THÔNG TIN CHUYỂN KHOẢN
Ngân hàng: ${row.bank_name || '---'}
Số TK: ${row.account_number || '---'}
Chủ TK: ${row.holder_name || '---'}
        `.trim();

        navigator.clipboard.writeText(textToCopy);
        setToast({ open: true, message: 'Đã sao chép thông tin TK!', severity: 'info' });
    };

    const columns = [
        { id: 'name', label: 'Tên gợi nhớ', minWidth: 150 },
        { id: 'type', label: 'Loại TK', minWidth: 100 },
        { id: 'bank', label: 'Ngân hàng', minWidth: 120 },
        { id: 'number', label: 'Số TK', minWidth: 130 },
        { id: 'holder', label: 'Chủ TK', minWidth: 150 },
        { id: 'status', label: 'Trạng thái', minWidth: 100 },
        { id: 'actions', label: 'Thao tác', minWidth: 120, align: 'center' },
    ];

    return (
        <Box>
            <DataTable
                columns={columns}
                data={data}
                renderRow={(row) => (
                    <TableRow key={row.id} hover>
                        <TableCell sx={{ fontWeight: 'bold', color: '#007373' }}>{row.name}</TableCell>
                        <TableCell>
                            <Chip 
                                label={ACCOUNT_TYPE_MAP[row.account_type]?.label || row.account_type} 
                                color={ACCOUNT_TYPE_MAP[row.account_type]?.color || 'default'} 
                                size="small" variant="outlined" 
                            />
                        </TableCell>
                        <TableCell>{row.bank_name}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>{row.account_number}</TableCell>
                        <TableCell>{row.holder_name}</TableCell>
                        <TableCell>
                            <Chip 
                                label={row.is_active ? 'Active' : 'Locked'} 
                                color={row.is_active ? 'success' : 'default'} 
                                size="small" 
                            />
                        </TableCell>
                        <TableCell align="center">
                            <Tooltip title="Sao chép thông tin">
                                <IconButton size="small" color="info" onClick={() => handleCopy(row)}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Sửa">
                                <IconButton size="small" color="primary" onClick={() => { setEditItem(row); setIsOpen(true); }}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Xóa">
                                <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </TableCell>
                    </TableRow>
                )}
            />

            <FinancialAccountDialog
                open={isOpen}
                onClose={() => { setIsOpen(false); onCloseAdd(); }}
                initialData={editItem}
                onSuccess={() => { setIsOpen(false); onCloseAdd(); loadData(); }}
            />

            <Snackbar 
                open={toast.open} 
                autoHideDuration={2000} 
                onClose={() => setToast({ ...toast, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={toast.severity} variant="filled">{toast.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default FinancialAccountTab;